import { formatMoney } from "@khazzin/i18n/money";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Button } from "@khazzin/ui/primitives/Button";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { cashRows, inHand } from "../../modules/cod/cod";
import { useOffline } from "../../offline/Offline";
import { isDone } from "./stops";

/**
 * Cash: the cash in hand (EP10-S19) — the wallet's collections as the last
 * sync read them plus the confirmations still queued on the phone, so the
 * figure is right with no signal — what today's stops will still collect,
 * and My Earnings (EP04-S11's summary, read online). COD is USD only and
 * drivers have no cash cap (owner, batch 02), so there is no lira figure and
 * no cap meter. **End of day** opens the handover (EP10-S20).
 */
/** My Earnings (EP04-S11's summary), read online; Cash and the Dashboard share it. */
export function useEarnings() {
	const { day, online } = useOffline();
	const api = useApi();
	const driverId = day?.driver.driverId ?? "";
	return useQuery({
		queryKey: ["driver", "earnings", driverId],
		queryFn: ({ signal }) => api.getDriverEarningsSummary(driverId, { signal }),
		enabled: online && driverId !== "",
	});
}

export function Cash() {
	const { t, locale } = useI18n();
	const { day, online, wallet, outbox, tenderOptions } = useOffline();
	const earnings = useEarnings();

	// The day also lists the stops finished today (EP10-S16); they collect nothing more.
	const toCollect = Object.values(day?.shipments ?? {}).reduce(
		(sum, s) =>
			sum +
			// A COD the company pays the store itself is not ours to carry (ADJ-1022-6).
			(s.cod?.currency === "USD" && s.codPaidToStore !== true && !isDone(s.status)
				? s.cod.amountMinor
				: 0),
		0,
	);
	const usd = (amountMinor: number, currency = "USD") =>
		formatMoney({ amountMinor, currency }, locale);
	const rows = cashRows(wallet, outbox, day?.shipments ?? {}, tenderOptions);
	const held = Object.entries(inHand(rows));

	return (
		<div className="flex flex-col gap-4">
			<section className="flex flex-col gap-2 card p-4">
				<h2 className="text-[13px] text-fg-muted">{t("driver.cash.inHand")}</h2>
				{(held.length === 0 ? [["USD", 0] as const] : held).map(([currency, amountMinor]) => (
					<p key={currency} className="text-[28px] font-bold text-fg">
						{usd(amountMinor, currency)}
					</p>
				))}
				{wallet === null && (
					<p className="text-[13px] text-fg-muted">{t("driver.cash.walletUnknown")}</p>
				)}
				<Button asChild variant="primary" size="lg">
					<Link to="/cash/handover">{t("driver.handover.open")}</Link>
				</Button>
				{rows.length > 0 && (
					<ul
						aria-label={t("driver.cash.collections")}
						className="flex flex-col divide-y divide-border"
					>
						{rows.map((r) => (
							<li key={r.shipmentId} className="flex flex-wrap items-center gap-2 py-2 text-[14px]">
								<Link
									to={`/today/${r.shipmentId}`}
									className="me-auto min-h-12 content-center font-bold text-fg"
								>
									{r.trackingNumber}
								</Link>
								<span className="text-fg">{usd(r.collectedMinor, r.currency)}</span>
								{r.collectedMinor !== r.expectedMinor && (
									<StatusChip tone="warning" size="sm">
										{t("driver.cash.variance", {
											amount: usd(r.collectedMinor - r.expectedMinor, r.currency),
										})}
									</StatusChip>
								)}
								<StatusChip tone={r.queued ? "neutral" : "success"} size="sm">
									{t(r.queued ? "driver.outcome.queued" : "driver.cash.synced")}
								</StatusChip>
							</li>
						))}
					</ul>
				)}
			</section>

			<section className="card p-4">
				<h2 className="text-[13px] text-fg-muted">{t("driver.cash.toCollect")}</h2>
				<p className="text-[24px] font-bold text-fg">{usd(toCollect)}</p>
			</section>

			<section className="flex flex-col gap-2 card p-4">
				<h2 className="text-[15px] font-bold text-fg">{t("driver.cash.earnings")}</h2>
				{earnings.data ? (
					<dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[14px]">
						<dt className="text-fg-muted">
							{t("driver.cash.earned", { count: earnings.data.deliveries })}
						</dt>
						<dd className="text-end text-fg">
							{usd(earnings.data.earningMinor, earnings.data.currency)}
						</dd>
						<dt className="text-fg-muted">{t("driver.cash.keptBack")}</dt>
						<dd className="text-end text-fg">
							{usd(earnings.data.recoveredMinor, earnings.data.currency)}
						</dd>
						<dt className="text-fg-muted">{t("driver.cash.shortfall")}</dt>
						<dd className="text-end text-fg">
							{usd(earnings.data.openShortfallMinor, earnings.data.currency)}
						</dd>
						<dt className="font-bold text-fg">{t("driver.cash.netOwed")}</dt>
						<dd className="text-end font-bold text-fg">
							{usd(earnings.data.netOwedMinor, earnings.data.currency)}
						</dd>
					</dl>
				) : (
					<p className="text-[14px] text-fg-muted">
						{t(
							!online
								? "driver.cash.earningsOffline"
								: earnings.isError
									? "driver.cash.earningsUnavailable"
									: "driver.today.loading",
						)}
					</p>
				)}
			</section>
		</div>
	);
}
